import { useAppLogoAsset } from "@shared/hooks/useAppLogoAsset";
import { useAuth } from "@shared/hooks/useAuth";
import { Box, Button, Typography } from "@mui/material";
import { Form } from "@shared/components/Form";
import { useSearchParams } from "react-router-dom";
import { AuthContainer, ErrorMessage } from "./Auth.styles";
import { toast } from "@shared/services/toast";
import { useCallback } from "react";
import { useDirectOAuth2Authorize } from "@queries/auth/useDirectOAuth2Authorize";
import { cookieService } from "@shared/services/cookie-service";
import { useAuthStore } from "@store/auth.store";
import { OAuthClientInfoBox } from "./components/OAuthClientInfoBox";
import { SubmitButton } from "@avenue-8/sidekick-ui";

type Props = {
  userEmail: string;
  isLoadingOAuthClient: boolean;
  oauthClientInfo:
    | {
        clientName: string;
        clientRedirectUrl: string;
      }
    | null
    | undefined;
};

export const OAuthAuthorization = (props: Props) => {
  const { userEmail, isLoadingOAuthClient, oauthClientInfo } = props;
  const { src: logoSrc, alt: logoAlt } = useAppLogoAsset();
  const { error } = useAuth();
  const [searchParams] = useSearchParams();
  const logout = useAuthStore((state) => state.logout);

  const directOAuth2AuthorizeMutation = useDirectOAuth2Authorize({
    onSuccess: (data) => {
      window.location.replace(data.redirectUrl);
    },
  });

  const handleDirectOAuth2Authorize = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (directOAuth2AuthorizeMutation.isPending) return;
      const oauthData = {
        clientId: searchParams.get("client_id")!,
        scope: searchParams.get("scope")!,
        redirectUri: searchParams.get("redirect_uri")!,
        state: searchParams.get("state")!,
        responseType: searchParams.get("response_type")!,
      };

      if (
        !oauthData.clientId ||
        !oauthData.scope ||
        !oauthData.redirectUri ||
        !oauthData.responseType
      ) {
        window.location.replace("/");
        toast.error("Invalid params. Try again later.", { theme: "light" });
        return;
      }

      directOAuth2AuthorizeMutation.mutate({
        ...oauthData,
      });
    },
    [searchParams, directOAuth2AuthorizeMutation],
  );

  const handleLoginAsDifferentUserClick: React.MouseEventHandler<
    HTMLButtonElement
  > = (e) => {
    e.preventDefault();
    logout();
    cookieService.deleteAllCookies();
  };

  return (
    <AuthContainer>
      <header>
        <img src={logoSrc} alt={logoAlt} />
      </header>
      <Form
        onSubmit={handleDirectOAuth2Authorize}
        aria-disabled={
          directOAuth2AuthorizeMutation.isPending ||
          directOAuth2AuthorizeMutation.isSuccess
        }
      >
        {error && <ErrorMessage>{error.message}</ErrorMessage>}
        <Box mt={3} mb={3}>
          <Typography variant="h6" textAlign={"center"}>
            You are signed in as
          </Typography>
          <Typography variant="body1" textAlign={"center"} fontWeight={500}>
            {userEmail}
          </Typography>
          <Box mt={4} mb={1}>
            <OAuthClientInfoBox
              isLoading={isLoadingOAuthClient}
              oauthClientInfo={oauthClientInfo}
              loggedIn
            />
          </Box>
        </Box>
        <SubmitButton
          isLoading={
            directOAuth2AuthorizeMutation.isPending ||
            directOAuth2AuthorizeMutation.isSuccess
          }
        >
          Yes, Continue
        </SubmitButton>
        <Button
          variant="text"
          type="button"
          onClick={handleLoginAsDifferentUserClick}
        >
          Sign in as a different user?
        </Button>
      </Form>
    </AuthContainer>
  );
};
